import { useEffect, useState } from 'preact/hooks';
import { init as initPageLoop } from '@pageloop/vanilla';
import { AuroraBg } from './components/AuroraBg';
import { Nav } from './components/Nav';
import { Hero } from './components/Hero';
import { Samples } from './components/Samples';
// LogoStrip ("teams shipping docs with PageLoop") hidden for now — no
// client logos to show yet. Kept in the tree for when we have them.
// import { LogoStrip } from './components/LogoStrip';
import { HowItWorks } from './components/HowItWorks';
import { FeatureTouts } from './components/FeatureTouts';
import { AgentLoop } from './components/AgentLoop';
// DemoStrip ("pageloop go" terminal + ticker) intentionally not rendered
// for now — kept in the tree for a planned redesign.
// import { DemoStrip } from './components/DemoStrip';
import { Pricing } from './components/Pricing';
import { Faq } from './components/Faq';
import { FinalCta } from './components/FinalCta';
import { Footer } from './components/Footer';
import { Signup } from './components/Signup';
import { Status } from './components/Status';
import { useCursorParallax } from './hooks/useCursorParallax';
import { PAGELOOP_DISABLED, PAGELOOP_ENDPOINT, PAGELOOP_PROJECT } from './data/pageloop-config';
import type { ComponentChildren } from 'preact';
// The renderer's stylesheet — must be on the page from first paint so
// bubbles/sidebar render without a flash of unstyled content.
import '@pageloop/vanilla/style.css';

/**
 * Marketing root. Two routes:
 *   `/`         — full landing page (hero → footer)
 *   `/signup`   — minimal signup page (plan-aware, kicks OAuth)
 *
 * Static-hosted SPA: any unknown path falls back to index.html and the
 * client-side switch below picks the route from `window.location.pathname`.
 * Avoids the weight of a router library; we have one branch.
 *
 * The aurora background is fixed at the body level — every section
 * stacks over it through the layered z-index in styles/tokens.
 */
export function App() {
	useCursorParallax();
	const [path, setPath] = useState(() =>
		typeof window === 'undefined' ? '/' : window.location.pathname,
	);

	useEffect(() => {
		// Sync state on browser-history navigation so the back button works
		// when a future page link uses pushState.
		const onPop = () => setPath(window.location.pathname);
		window.addEventListener('popstate', onPop);
		return () => window.removeEventListener('popstate', onPop);
	}, []);

	// Pick the page first, then wrap everything in <PageLoopShell> so
	// the comment widget hangs off body once and overlays every route.
	// Mounting the provider at the App level (not per-route) means
	// bubbles + the toolbar survive client-side route swaps without
	// remounting.
	let page: ComponentChildren;
	if (path === '/signup' || path.startsWith('/signup/')) {
		page = (
			<>
				<AuroraBg />
				<Nav />
				<main>
					<Signup />
				</main>
				<Footer />
			</>
		);
	} else if (path === '/status' || path.startsWith('/status/')) {
		page = (
			<>
				<AuroraBg />
				<Nav />
				<main>
					<Status />
				</main>
				<Footer />
			</>
		);
	} else {
		page = (
			<>
				<AuroraBg />
				<Nav />
				<main>
					<Hero />
					<Samples />
					<HowItWorks />
					<FeatureTouts />
					<AgentLoop />
					<Pricing />
					<Faq />
					<FinalCta />
				</main>
				<Footer />
			</>
		);
	}

	return <PageLoopShell>{page}</PageLoopShell>;
}

/**
 * Wraps the marketing site in a PageLoop widget when the env-keyed
 * flag is on (which is the default in production builds). The widget
 * binds to the demo project and lets visitors leave public comments
 * on any text or element. `PAGELOOP_DISABLED=1` short-circuits to a
 * plain passthrough — useful during local dev when you don't want a
 * background WebSocket reconnecting on every save.
 */
function PageLoopShell({ children }: { children: ComponentChildren }) {
	useEffect(() => {
		if (PAGELOOP_DISABLED) return;
		// Mount the vanilla widget once at the app root so bubbles + the
		// toolbar overlay every route and survive client-side navigation.
		// We use the renderer entry directly — the same `init()` real
		// embedders call — rather than a framework wrapper: the wrappers
		// pull the renderer in via a dynamic `import('@pageloop/vanilla')`
		// that can't be resolved from inside @pageloop/client under the
		// workspace's strict node_modules layout.
		const instance = initPageLoop({
			endpoint: PAGELOOP_ENDPOINT,
			projectId: PAGELOOP_PROJECT,
			// Default UI knobs — the demo project's server-side settings can
			// override any of these; this is just the boot state.
			ui: { theme: 'auto' },
		});
		// Bridge the widget's auth state to a window event so the marketing
		// nav (useWidgetSession) can re-check "am I signed in?" the moment a
		// visitor logs in/out through the on-page widget — no reload needed.
		const offAuth = instance.bus.on('auth:changed', () => {
			window.dispatchEvent(new CustomEvent('pageloop:auth-maybe-changed'));
		});
		return () => {
			offAuth();
			void instance.stop();
		};
	}, []);
	return <>{children}</>;
}
